import { Card } from "@/components/ui/card";
import { ArrowUpCircle, CheckCircle2, Circle, FileUp, Trophy, Megaphone, ClipboardList } from "lucide-react";
import { STATUS_LABEL, type DispatchStatus } from "@/hooks/useUpdateDispatchStatus";

interface Props {
  load: any;
  dispatch: any | null;
  events: any[];
}

interface TimelineItem {
  id: string;
  ts: string;
  label: string;
  actor: string;
  notes?: string;
  kind: "post" | "award" | "dispatch_created" | "status" | "document" | "other";
}

function fmtTs(iso: string) {
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return d.toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

function fullTs(iso: string) {
  return new Date(iso).toLocaleString("en-US", { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

function eventLabel(e: { event_type: string; payload: any }) {
  if (e.event_type.startsWith("status_")) {
    const s = e.event_type.replace("status_", "") as DispatchStatus;
    return `Status → ${STATUS_LABEL[s] ?? s}`;
  }
  if (e.event_type === "document_uploaded") return `Uploaded ${e.payload?.file_name ?? "document"}`;
  return e.event_type.replace(/_/g, " ");
}

function iconFor(kind: TimelineItem["kind"], eventType?: string) {
  if (kind === "post") return <Megaphone className="h-3.5 w-3.5 text-brand-teal" />;
  if (kind === "award") return <Trophy className="h-3.5 w-3.5 text-brand-teal" />;
  if (kind === "dispatch_created") return <ClipboardList className="h-3.5 w-3.5 text-brand-teal" />;
  if (kind === "document") return <FileUp className="h-3.5 w-3.5 text-sky-600" />;
  if (kind === "status") {
    if (eventType?.includes("delivered") || eventType?.includes("closed"))
      return <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />;
    return <ArrowUpCircle className="h-3.5 w-3.5 text-brand-teal" />;
  }
  return <Circle className="h-3.5 w-3.5 text-muted-foreground" />;
}

export function TimelineTab({ load, dispatch, events }: Props) {
  const items: TimelineItem[] = [];

  if (load?.posted_at) {
    items.push({ id: `posted-${load.id}`, ts: load.posted_at, label: "Load posted", actor: "Shipper", kind: "post" });
  }
  if (load?.awarded_at) {
    items.push({ id: `awarded-${load.id}`, ts: load.awarded_at, label: "Broker awarded", actor: "Shipper", kind: "award" });
  }
  if (dispatch?.created_at) {
    items.push({ id: `dispatch-${dispatch.id}`, ts: dispatch.created_at, label: "Dispatch packet created", actor: "Shipper", kind: "dispatch_created" });
  }
  for (const e of events) {
    items.push({
      id: e.id,
      ts: e.created_at,
      label: eventLabel(e),
      actor: e.actor_name ?? "System",
      notes: e.payload?.notes ?? undefined,
      kind: e.event_type?.includes("document") ? "document" : e.event_type?.startsWith("status_") ? "status" : "other",
    });
  }

  items.sort((a, b) => new Date(b.ts).getTime() - new Date(a.ts).getTime());

  if (items.length === 0) {
    return (
      <Card className="p-6 text-center">
        <Circle className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
        <p className="text-sm text-muted-foreground">No activity yet.</p>
      </Card>
    );
  }

  return (
    <Card className="p-4">
      <ol className="relative border-l border-border ml-2 space-y-3">
        {items.map((it) => (
          <li key={it.id} className="pl-4 relative">
            <span className="absolute -left-[7px] top-0.5 bg-card rounded-full">
              {iconFor(it.kind, it.id)}
            </span>
            <div className="text-sm font-medium text-foreground">{it.label}</div>
            <div className="text-[11px] text-muted-foreground flex items-center gap-1.5">
              <span>{it.actor}</span>
              <span>·</span>
              <span title={fullTs(it.ts)}>{fmtTs(it.ts)}</span>
            </div>
            {it.notes && <div className="text-xs text-muted-foreground italic mt-0.5">"{it.notes}"</div>}
          </li>
        ))}
      </ol>
    </Card>
  );
}
